import type { NuxtApp } from 'nuxt/app';
import { useNotificationStore } from '~~/common/stores/notification';
import { useModalStore } from '~~/common/stores/modal';
import { helpers } from '@vuelidate/validators';
import { isApiError } from '~~/common/utils/apiError';
import type { ErrorObject } from '~~/common/utils/apiError';
import type { PostTranslationPluginProvides } from '~~/common/plugins/post-translation';

export interface NotificationText {
  title?: string | {
    message: string;
  };
  titleToken?: string;
  titleParams?: Record<string, unknown>;
  subtitle?:string | {
    message: string;
  };
  subtitleToken?: string;
  subtitleParams?: Record<string, unknown>;
  footer?: string;
  footerToken?: string;
  footerParams?: Record<string, unknown>;
  tooltip?: string;
  tooltipToken?: string;
  tooltipParams?: Record<string, unknown>;
}

export interface NotificationInput {
  text?: NotificationText;
}

interface NotificationContext {
  awt: PostTranslationPluginProvides['awt'];
}

interface TokenizedResult {
  title?: string;
  subtitle?: string;
  footer?: string;
  tooltip?: string;
}

export function notificationTokenizer ({ text }: NotificationInput, { awt }: NotificationContext): TokenizedResult {
  const textObject = text || {};
  const result: TokenizedResult = {};
  for (const key of [
    'title',
    'subtitle',
    'footer',
    'tooltip',
  ] as const) {
    const nonTokenText = textObject[key]; // title
    const token = textObject[key + 'Token' as keyof NotificationText] as NotificationText['titleToken']; // titleToken
    const params = textObject[key + 'Params' as keyof NotificationText] as NotificationText['titleParams']; // titleParams
    result[key] = token ? awt(token, params) : typeof nonTokenText === 'string' ? nonTokenText : nonTokenText?.message;
  }
  return result;
}

interface CollectPayloadParams {
  nuxtApp: NuxtApp;
  error: string | ErrorObject | unknown;
  manualClose: boolean;
  enableTitleMerge: boolean;
  useSubtitle: boolean;
}

interface PayloadResult {
  httpCode?: number;
  manualClose: boolean;
  mainTitle: string;
  text: {
    title: string;
    subtitle: string;
  };

  [key: string]: unknown;
}

function collectPayload ({ nuxtApp, error, manualClose, enableTitleMerge, useSubtitle }: CollectPayloadParams): PayloadResult {
  const $awt = nuxtApp.$awt || function (arg: string) {
    /* eslint-disable-next-line no-console */
    console.warn('awt is not found!, nuxtApp is probably not defined');
    return arg;
  };
  let title = typeof error === 'string' ? error : '';
  let mainTitle = title; // for AwApiConfirmModal.vue
  let subtitle = '';
  let titleMerge = false;

  const errorObj = isApiError(error) ? error : undefined;
  if (errorObj?.response) {
    if (errorObj.response?.data?.title) {
      title = errorObj.response.data.title;
      titleMerge = true;
    }
    if (errorObj.response?.data?.message) {
      const newTitleValue = (titleMerge && enableTitleMerge ? `${title} ` : '') + errorObj.response.data.message;

      if (useSubtitle) {
        subtitle = newTitleValue;
      } else {
        title = newTitleValue;
        titleMerge = true;
        mainTitle = title;
      }
    } else if (errorObj.response?.data?.detail) { // for dealer instance response
      subtitle = errorObj.response.data.detail;
      mainTitle = title;
    }
    if (errorObj.response?.data?.messageToken) {
      title = (titleMerge && enableTitleMerge ? `${title} ` : '') + $awt(errorObj.response.data.messageToken);
      titleMerge = true;
      mainTitle = title;
    }
    // if error contain detailed information
    if (Array.isArray(errorObj.response?.data?.errors)) {
      errorObj.response.data.errors.forEach((e) => {
        title = (titleMerge && enableTitleMerge ? `${title} ` : '') + e.message;
        // because this message type is too long
        manualClose = true;
      });
    }
  }

  return {
    httpCode: errorObj?.response?.status,
    manualClose,
    mainTitle,
    text: {
      title,
      subtitle,
    },
  };
}

export interface SetErrorNotificationOptions {
  nuxtApp: NuxtApp;
  error: unknown;
  manualClose?: boolean;
  closable?: boolean;
  forceModal?: boolean;
  fromSelfcare?: boolean;
  enableTitleMerge?: boolean;
  fromModal?: boolean;
}

export interface ErrorNotificationResult {
  name: string;
  message: string | undefined;
}

// The api/coldParcel confirm modals resolve generically ({ name, message }), but in this flow
// they are always resolved with a string name ('accept'/'cancel') and the resolveOption message
// — narrow that generic modal result back to the ErrorNotificationResult contract.
function toErrorNotificationResult (result: { name?: unknown; message?: unknown }): ErrorNotificationResult {
  return {
    name: typeof result.name === 'string' ? result.name : 'accept',
    message: typeof result.message === 'string' ? result.message : undefined,
  };
}

/**
 * @returns Promise<{ name: string, message: string|undefined }>
 * */
export function setErrorNotification ({
  nuxtApp,
  error,
  manualClose = false,
  closable = true,
  forceModal = false,
  fromSelfcare = false,
  enableTitleMerge = true,
  fromModal = false,
}: SetErrorNotificationOptions): Promise<ErrorNotificationResult> | null {
  const pinia = nuxtApp.$pinia;
  const modalStore = useModalStore(pinia);
  const notificationStore = useNotificationStore(pinia);
  const payload = collectPayload({ nuxtApp, error, manualClose, enableTitleMerge, useSubtitle: false });
  const resolveOption = ({ 428: 'auto', 412: 'force' })[payload.httpCode || 0];

  const errorObj = isApiError(error) ? error : undefined;

  // open confirm modal and save API data for second API call
  if ([422, 428].includes(payload.httpCode || 0) || forceModal) { // Unprocessable Entity, Precondition Required
    if (import.meta.server) {
      return new Promise((resolve) => {
        resolve({ name: 'accept', message: resolveOption });
      });
    }

    if (errorObj?.response?.data?.scope === 'coldParcel') {
      modalStore.showColdParcelConfirmModal({
        data: {
          resolveOption,
          errorData: {
            ...payload,
            error_scope: errorObj?.response?.data?.scope,
            itemReferences: errorObj?.response?.data?.itemReferences,
            fromModal,
          },
        },
        closable,
      });

      const coldParcelPromise = modalStore.lastColdParcelConfirmModal.promise;
      return coldParcelPromise ? coldParcelPromise.then(toErrorNotificationResult) : null;
    } else {
      modalStore.showApiConfirmModal({
        data: {
          resolveOption,
          errorData: {
            ...payload,
            errors: errorObj?.response?.data?.errors,
            error_scope: errorObj?.response?.data?.scope,
          },
          fromSelfcare,
        },
        closable: payload.httpCode === 422 ? false : closable,
      });

      const apiConfirmPromise = modalStore.lastApiConfirmModal.promise;
      return apiConfirmPromise ? apiConfirmPromise.then(toErrorNotificationResult) : null;
    }

  } else if (payload.httpCode !== 412) {
    notificationStore.pushError({
      text: payload.text,
      manualClose: payload.manualClose,
      screenReaderOnly: false,
      promise: null,
    });
  }

  return Promise.resolve({ name: 'accept', message: resolveOption });
}

export interface ExtendedPayload extends PayloadResult {
  errorData?: Record<string, unknown>;
  apiData?: Record<string, unknown>;
  type?: string;
  iconName?: string;

  [key: string]: unknown;
}

export function getErrorNotification (nuxtApp: NuxtApp, error: unknown, manualClose = false, apiData: unknown = false, useSubtitle = false): ExtendedPayload {
  const errorObj = isApiError(error) ? error : undefined;
  let payload: ExtendedPayload = collectPayload({ nuxtApp, error, manualClose, enableTitleMerge: false, useSubtitle });

  //
  if (payload.httpCode === 428) { // Precondition Required
    const isColdParcelError = errorObj?.response?.data?.scope === 'coldParcel';
    payload = {
      ...(typeof error === 'object' && error !== null ? error as Record<string, unknown> : {}),
      ...payload,
      errorData: {
        ...payload,
        errors: errorObj?.response?.data?.errors,
      },
      apiData: {
        ...(apiData as Record<string, unknown>),
        ...((apiData as Record<string, unknown>)?.storeActionParameters as Record<string, unknown>),
        resolveOption: 'auto',
      },
      type: isColdParcelError ? 'error' : 'info',
      iconName: isColdParcelError ? 'error-16' : 'education-16',
      manualClose: false,
    };
  } else if (payload.httpCode === 412) { //  Precondition Failed
    payload = {
      ...payload,
      errorData: payload,
      apiData: {
        ...(apiData as Record<string, unknown>),
        ...((apiData as Record<string, unknown>)?.storeActionParameters as Record<string, unknown>),
        resolveOption: 'force',
      },
    };
  }
  return {
    type: 'error',
    iconName: 'error-16',
    ...payload,
  };
}

function getErrorConstraintValidations (error: unknown): Record<string, string[]> | false {
  const errorObj = isApiError(error) ? error : undefined;
  if (errorObj?.response?.data?.scope !== 'data_validation') {
    return false;
  }

  const propertyViolations: Record<string, string[]> = {};
  const violations = errorObj.response?.data?.constraintViolations || errorObj.response?.data?.violations || {};

  for (const v in violations) {
    if (!Object.prototype.hasOwnProperty.call(violations, v)) {
      continue;
    }

    const violation = violations[v];
    if (!violation) continue;

    const key = violation.propertyPath;
    if (typeof propertyViolations[key] === 'undefined') {
      propertyViolations[key] = [];
    }
    propertyViolations[key].push(violation.message);
  }

  return propertyViolations;
}

interface FormVm {
  backendValidation: {
    errors: Record<string, string[]>;
    propertyPaths: Record<string, string>;
  };

  [key: string]: unknown;
}

// The form view model as accepted by `setValidationFromError`, including the optional
// `apiError` it may write back. Exported so callers can type their own `formVm` payloads
// without duplicating the (non-exported) `FormVm` shape.
export type ValidatableFormVm = FormVm & { apiError?: unknown };

interface FieldVm {
  name: string;

  [key: string]: unknown;
}

export function backendValidator (formVm: FormVm, validateIf?: () => boolean) {
  return (value: unknown, fieldVm: FieldVm) => {
    const propertyViolations = formVm.backendValidation.errors;
    const propertyPath = formVm.backendValidation.propertyPaths[fieldVm.name];
    if (typeof formVm[fieldVm.name] === 'object' && formVm[fieldVm.name] && propertyPath) {
      const field = formVm[fieldVm.name] as Record<string, unknown>;
      if (!field.tokens) {
        field.tokens = {};
      }
      (field.tokens as Record<string, string>).backend = (propertyViolations[propertyPath] || []).join(' | ');
    }
    return !value || !propertyPath || !propertyViolations || (validateIf && !validateIf()) || (typeof propertyViolations[propertyPath] === 'undefined');
  };
}

export function backendValidatorWithMessage (formVm: FormVm, name: string, validateIf?: () => boolean) {
  let message = '';

  return helpers.withMessage(
    ({ $invalid }: { $invalid: boolean }) => ($invalid
      ? message
      : ''
    ),
    (value: unknown) => {
      const propertyViolations = formVm.backendValidation.errors;
      const propertyPath = formVm.backendValidation.propertyPaths[name];
      if (typeof formVm[name] !== 'undefined' && propertyPath) {
        message = (propertyViolations[propertyPath] || []).join(' | ');
      }
      return !value || !propertyPath || !propertyViolations || (validateIf && !validateIf()) || (typeof propertyViolations[propertyPath] === 'undefined');
    },
  );
}

function getErrorTypeTokenError (error: unknown): Record<string, string[]> | false {
  const errorObj = isApiError(error) ? error : undefined;
  const errorData = errorObj?.response?.data || {};
  if (!errorData?.errorTypeToken) {
    return false;
  }
  const errorTypeTokenMap: Record<string, string> = {
    'user.error.wrong_current_password': 'currentPassword',
    'user.error.same_passwords': 'password',
    'user.error.passwords_no_match': 'confirmPassword',
    'user.error.repeated_password': 'password',
  };

  const result: Record<string, string[]> = {};
  const fieldName = errorTypeTokenMap[errorData.errorTypeToken] || null;
  if (!fieldName) {
    return false;
  }
  result[fieldName] = [errorData.message || ''];
  return result;
}

export function setValidationFromError (formVm: ValidatableFormVm, error: unknown, nuxtApp: NuxtApp): void {
  const constraintViolations = getErrorConstraintValidations(error);
  const errorTypeTokenErrors = getErrorTypeTokenError(error);
  if (constraintViolations || errorTypeTokenErrors) {
    if (constraintViolations !== false) {
      formVm.backendValidation.errors = constraintViolations;
    }
    if (errorTypeTokenErrors !== false) {
      formVm.backendValidation.errors = errorTypeTokenErrors;
    }
  } else {
    formVm.apiError = getErrorNotification(nuxtApp, error);
  }
}

interface CategoryItem {
  id: string | number;
  children?: CategoryItem[];

  [key: string]: unknown;
}

// Find an element in an array recursively
export function findCategoryById (id: string | number, arr: CategoryItem[]): CategoryItem | null {
  return arr.reduce<CategoryItem | null>((a, item) => {
    if (a) {
      return a;
    }
    if (item.id === id) {
      return item;
    }
    if (item.children) {
      return findCategoryById(id, item.children);
    }
    return null;
  }, null);
}
